import React, { useState } from "react";
import {
  Container,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
} from "@mui/material";
import { url } from "../../constants/url";

const UserRegistration = ({ userData, onRegistered }) => {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    address: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showCompanyPrompt, setShowCompanyPrompt] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${url}/api/users/update-user/${userData._id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Failed to update user");
      setShowCompanyPrompt(true);
      onRegistered && onRegistered();
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  if (showCompanyPrompt) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Card 
          sx={{ 
            borderRadius: 3,
            boxShadow: "0px 8px 40px rgba(0,0,0,0.1)",
            border: "1px solid #e0e0e0"
          }}
        >
          <CardContent sx={{ p: 4, textAlign: "center" }}>
            <Typography 
              variant="h5" 
              align="center" 
              gutterBottom 
              sx={{ 
                fontWeight: 600,
                color: "text.primary",
                mb: 3
              }}
            >
              Profile Completed Successfully!
            </Typography>
            
            {userData?.company?.length === 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography 
                  variant="body1" 
                  align="center" 
                  sx={{ 
                    color: "text.secondary",
                    mb: 3
                  }}
                >
                  Would you like to add your company now?
                </Typography>
                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  onClick={() => window.location.replace("/add-company")}
                  sx={{
                    py: 1.5,
                    borderRadius: 2,
                    bgcolor: "success.main",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "1rem",
                    "&:hover": {
                      bgcolor: "success.dark",
                      transform: "translateY(-1px)",
                      boxShadow: 4
                    },
                    transition: "all 0.2s ease-in-out",
                    mb: 2
                  }}
                >
                 Go to Company
                </Button>
              </Box>
            )}
            
            <Button
              variant="outlined"
              fullWidth
              size="large"
              onClick={() => window.location.replace("/dashboard")}
              sx={{
                py: 1.5,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                fontSize: "1rem",
                borderColor: "primary.main",
                color: "primary.main",
                "&:hover": {
                  borderColor: "primary.dark",
                  backgroundColor: "primary.light",
                  transform: "translateY(-1px)",
                },
                transition: "all 0.2s ease-in-out"
              }}
            >
              Go to Dashboard
            </Button>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{  }}>
      <Card 
        sx={{ 
          borderRadius: 3,
          boxShadow: "0px 8px 40px rgba(0,0,0,0.1)",
          border: "1px solid #e0e0e0",
          background: "white"
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Typography 
            variant="h4" 
            align="center" 
            gutterBottom 
            sx={{ 
              fontWeight: 700,
              color: "text.primary",
              mb: 1
            }}
          >
            Complete Your Profile
          </Typography>
          
          <Typography 
            variant="body1" 
            align="center" 
            sx={{ 
              color: "text.secondary",
              mb: 4
            }}
          >
            Please provide your details to continue
          </Typography>
          
          <form onSubmit={handleSubmit}>
            <TextField
              label="Full Name"
              name="name"
              fullWidth
              required
              value={form.name}
              onChange={handleChange}
              sx={{ 
                mb: 3,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  backgroundColor: "grey.50",
                  "&:hover fieldset": {
                    borderColor: "primary.main",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "primary.main",
                  }
                }
              }}
              placeholder="Enter your full name"
            />
            
            <TextField
              label="Phone Number"
              name="phone"
              fullWidth
              required
              value={form.phone}
              onChange={handleChange}
              sx={{ 
                mb: 3,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  backgroundColor: "grey.50",
                  "&:hover fieldset": {
                    borderColor: "primary.main",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "primary.main",
                  }
                }
              }}
              placeholder="Enter your phone number"
            />
            
            <TextField
              label="Address"
              name="address"
              fullWidth
              required
              multiline
              rows={3}
              value={form.address}
              onChange={handleChange}
              sx={{ 
                mb: 3,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  backgroundColor: "grey.50",
                  "&:hover fieldset": {
                    borderColor: "primary.main",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "primary.main",
                  }
                }
              }}
              placeholder="Enter your complete address"
            />
            
            {error && (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 3,
                  borderRadius: 2
                }}
              >
                {error}
              </Alert>
            )}
            
            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={loading || !form.name || !form.phone || !form.address}
              sx={{
                py: 1.5,
                borderRadius: 2,
                bgcolor: "success.main",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "1rem",
                boxShadow: "0px 4px 12px rgba(0,0,0,0.1)",
                "&:hover": {
                  bgcolor: "success.dark",
                  boxShadow: "0px 6px 16px rgba(0,0,0,0.2)",
                  transform: "translateY(-2px)",
                },
                "&:disabled": {
                  bgcolor: "grey.300",
                  transform: "none",
                  boxShadow: "none"
                },
                transition: "all 0.2s ease-in-out"
              }}
            >
              {loading ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={20} sx={{ color: "white" }} />
                  Saving...
                </Box>
              ) : (
                "Complete Registration"
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </Container>
  );
};

export default UserRegistration;